import { addToDictionary, ESdChangeReason, getDictionaryValueByKey } from "@kuika/kuika-cl-model";
import { IHappyPathPhaseModel, IWorkFlow } from "@kuika/kuika-cl-model/src/models/workflow";
import CommandInterceptor from "diagram-js/lib/command/CommandInterceptor";
import EventBus from "diagram-js/lib/core/EventBus";
import { Guid } from "guid-typescript";
import { ScreenDesignContextProvider } from "../../../../contexts/screen-design-context/screen-design-context-provider";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import type {
  IPhaseDeclaration,
  IPhaseLayoutPlan,
  IPhaseLayoutStep
} from "../../../../shared/utility/workflow-phase-layout";
import { planPhaseColumns } from "../../../../shared/utility/workflow-phase-layout";

/**
 * Happy Path phases as NATIVE `bpmn:Group` elements ("phase groups"), marked with the moddle
 * extension attrs `custom:isHappyPathPhase` / `custom:phaseId` (custom.json). Groups give every
 * interaction for free — create, move, resize handles, double-click rename (direct editing),
 * context-pad delete, selection, undo/redo, copy — and persist natively in the workflow XML, so
 * phase geometry is shared and versioned instead of living in localStorage. Groups are visual
 * artifacts: they never enter the JSON node model (workflow-sync's type switch skips them), are
 * not FlowNodes (lint + the shapeless-orphan prune ignore them), and codegen reads the JSON.
 *
 * This interceptor keeps the phase groups and the REAL happy-path model in sync — the same model
 * the Happy Path right panel edits (`wf.happyPathDefinition` + `step.extraParameters["happyPath"]`,
 * written through `saveWorkFlow` exactly like the panel), so canvas and panel never diverge:
 *   • group created   → phase entry appended (panel AddNewStatus semantics)
 *   • group renamed   → `happyPathPhase` updated (panel StatusUpdate semantics)
 *   • group deleted   → phase entry removed + orders renumbered (panel DeleteStatus semantics)
 *   • group/step moved or resized → `phaseOrder` follows the groups' left-to-right order, and each
 *     step is assigned to the phase group whose X-range contains its centre.
 * Geometry follows STRICT TILING — the phases are simply a vertical SPLIT of the pool
 * (`retilePhases`): they always cover it exactly (no gaps, no overlap, full height). The first
 * phase covers the whole pool; creating another splits the column under the created shape's
 * centre (`applyCreatePlacement`); resizing moves the SHARED boundary (the neighbour gives/takes
 * the difference); moving a column reorders it (centre order); deleting lets the remaining
 * columns absorb the space proportionally. A step dropped straddling a boundary is nudged into
 * its centre-owner column so assignment is never visually ambiguous.
 *
 * On `import.done` the two sides reconcile: model phases with no group are materialized as groups
 * tiled over the pool (legacy workflows defined via the panel), and marked groups with no entry get
 * one. `custom:phaseId` is the join key, so renames/reorders on either side stay linked.
 */

const PHASE_KEY: string = "happyPath";
const DEFAULT_PHASE_WIDTH: number = 260;
/** Floor for a phase column — wide enough that a default "Phase N" title never truncates (KOA-18280). */
const MIN_PHASE_WIDTH: number = 120;
const MIN_POOL_HEIGHT: number = 10;
const STEP_SNAP_PAD: number = 8;
/** Height of the header band drawn ABOVE the column (outside the pool) — the drag/select handle. */
export const PHASE_HEADER_HEIGHT: number = 28;
/** Shape types that carry a happy-path assignment and get edge-snapped into/out of phase columns. */
const STEP_TYPES: ReadonlySet<string> = new Set<string>([
  "bpmn:Task",
  "bpmn:UserTask",
  "bpmn:SendTask",
  "bpmn:SubProcess",
  "bpmn:CallActivity"
]);

/** bpmn:Group label companion — the rendered label lives in `categoryValueRef.value`. */
interface ICategoryValueLike {
  value?: string;
}

interface IBusinessObject {
  $type: string;
  name?: string;
  isHappyPathPhase?: boolean;
  phaseId?: string;
  /** For a `bpmn:Group`, bpmn-js's LabelUtil reads/writes the visible label HERE, never on `name`. */
  categoryValueRef?: ICategoryValueLike;
}

interface IShapeLike {
  id: string;
  type: string;
  x: number;
  y: number;
  width: number;
  height: number;
  businessObject?: IBusinessObject;
  labelTarget?: unknown;
  /** External label shape (groups have one) — repositioned into the header band. */
  label?: IShapeLike;
}

interface IBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface IResizeHints {
  kuikaPhasePin?: boolean;
}

interface IPoint {
  x: number;
  y: number;
}

interface IModelingLike {
  resizeShape(shape: IShapeLike, bounds: IBounds, minBounds?: IBounds | null, hints?: IResizeHints): void;
  createShape(shape: IShapeLike, position: IPoint, parent: IShapeLike): IShapeLike;
  moveShape(shape: IShapeLike, delta: IPoint, newParent?: IShapeLike | null, hints?: IResizeHints): void;
  updateLabel(element: IShapeLike, label: string): void;
}

interface IElementFactoryLike {
  createShape(attrs: { type: string; businessObject: IBusinessObject; width?: number; height?: number }): IShapeLike;
}

interface IBpmnFactoryLike {
  create(type: string): IBusinessObject;
}

interface IElementRegistryLike {
  get(id: string): IShapeLike | undefined;
  filter(fn: (e: IShapeLike) => boolean): IShapeLike[];
}

interface ICanvasLike {
  getRootElement(): IShapeLike;
  addMarker(element: IShapeLike | string, marker: string): void;
  removeMarker(element: IShapeLike | string, marker: string): void;
  viewbox(): { scale: number };
  /** diagram-js canvas layer — lives inside the transformed viewport (native zoom/pan alignment). */
  getLayer(name: string, index: number): SVGElement;
}

interface IDirectEditingLike {
  activate(element: IShapeLike): void;
}

interface IHitBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface IInteractionEventsLike {
  removeHits(gfx: SVGElement): void;
  createDefaultHit(element: IShapeLike, gfx: SVGElement): void;
  createBoxHit(gfx: SVGElement, type: string, attrs: IHitBox): void;
}

interface IHitContext {
  element?: IShapeLike;
  gfx?: SVGElement;
}

interface ICreateEndEvent {
  context?: { shape?: IShapeLike };
}

interface ISelectionChangedEvent {
  newSelection?: IShapeLike[];
}

interface IOverlayPosition {
  top?: number;
  left?: number;
  right?: number;
  bottom?: number;
}

interface IOverlayDefinition {
  position: IOverlayPosition;
  html: string | HTMLElement;
  scale?: boolean;
}

/** In-flight drag of the shared boundary between two adjacent phase columns. */
interface IBoundaryDrag {
  leftId: string;
  startClientX: number;
  leftStartX: number;
  leftStartWidth: number;
  pairWidth: number;
  scale: number;
  handleEl: HTMLElement;
  poolTop: number;
  poolBottom: number;
}

interface IOverlaysLike {
  add(element: IShapeLike | string, type: string, definition: IOverlayDefinition): string;
  remove(filter: { type: string }): void;
}

/** Live-move event payload (diagram-js `shape.move.move`): x/y are the shape's current position. */
interface IMoveEvent {
  shape?: IShapeLike;
  x?: number;
  y?: number;
}

interface ICommandContext {
  shape?: IShapeLike;
  element?: IShapeLike;
  shapes?: IShapeLike[];
  hints?: IResizeHints;
  /** Stored by diagram-js's ResizeShapeHandler — the bounds before the resize. */
  oldBounds?: IBounds;
}

/**
 * Unique default name for a new phase: one past the highest "Phase N" among the names already
 * taken. Position-independent by design — deriving the number from the new phase's x-order made
 * a phase dropped left of "Phase 1" index 0 and duplicated the name (KOA-18279).
 */
export function nextPhaseName(takenNames: readonly string[]): string {
  let max: number = 0;
  for (const name of takenNames) {
    const match: RegExpMatchArray | null = (name || "").trim().match(/^Phase (\d+)$/i);
    if (match) max = Math.max(max, parseInt(match[1], 10));
  }
  return `Phase ${max + 1}`;
}

/**
 * The label bpmn-js actually renders for a phase group. For a `bpmn:Group` the label lives in
 * `categoryValueRef.value` (LabelUtil.setLabel writes it there) — `bo.name` is only a stale
 * in-memory mirror that `modeling.updateLabel` never touches, so deriving the model name from
 * `bo.name` drifts `happyPathDefinition` away from the canvas title (KOA-15840: the phase-panel /
 * diagram name mismatch). Falls back to `bo.name` for a group materialized name-first (its label
 * element does not exist yet). Returns the raw label; callers trim / default as they need.
 */
export function phaseGroupLabel(element: IShapeLike | undefined): string {
  const bo: IBusinessObject | undefined = element?.businessObject;
  return bo?.categoryValueRef?.value ?? bo?.name ?? "";
}

export function isPhaseGroup(element: IShapeLike | undefined): boolean {
  if (!element || element.labelTarget) return false;
  const bo: IBusinessObject | undefined = element.businessObject;
  return !!bo && bo.$type === "bpmn:Group" && bo.isHappyPathPhase === true;
}

export default class HappyPathPhaseInterceptor extends CommandInterceptor {
  private readonly providerInstance: ScreenDesignContextProvider;
  /**
   * True while {@link materializeMissingGroups} is creating the columns a stored model
   * declares. The create hook consults it and stands aside: that path already knows every
   * column's rectangle, and the interactive placement rule would re-decide it.
   */
  private materializing: boolean = false;

  private readonly modeling: IModelingLike;

  private readonly elementRegistry: IElementRegistryLike;

  private readonly elementFactory: IElementFactoryLike;

  private readonly bpmnFactory: IBpmnFactoryLike;

  private readonly canvas: ICanvasLike;

  private readonly directEditing: IDirectEditingLike;

  /** Re-entrancy guard: our own pin-resize must not re-trigger the sync. */
  private pinning: boolean = false;

  /** Phase column currently highlighted as the drop target of an in-flight step drag. */
  private dropTarget: IShapeLike | undefined;

  /** Phase column highlighted because the (single) selected step belongs to it. */
  private ownerHighlight: IShapeLike | undefined;

  private readonly overlays: IOverlaysLike;

  /** Serialized per-phase step counts of the last badge render — skips overlay churn when unchanged. */
  private lastCountsKey: string = "";

  /** Column ids (in order) of the last boundary-grip render — grips rebuild only when the set changes. */
  private boundaryHandlesKey: string = "";

  private boundaryDrag: IBoundaryDrag | null = null;

  /** Guide line shown in the preview canvas layer while a boundary grip is dragged. */
  private previewLine: SVGElement | null = null;

  constructor(
    eventBus: EventBus,
    modeling: IModelingLike,
    elementRegistry: IElementRegistryLike,
    elementFactory: IElementFactoryLike,
    bpmnFactory: IBpmnFactoryLike,
    canvas: ICanvasLike,
    directEditing: IDirectEditingLike,
    interactionEvents: IInteractionEventsLike,
    overlays: IOverlaysLike,
    providerInstance: ScreenDesignContextProvider
  ) {
    super(eventBus);
    this.providerInstance = providerInstance;
    this.modeling = modeling;
    this.elementRegistry = elementRegistry;
    this.elementFactory = elementFactory;
    this.bpmnFactory = bpmnFactory;
    this.canvas = canvas;
    this.directEditing = directEditing;
    this.overlays = overlays;

    // Selecting a single step softly highlights its owning phase column — an instant answer to
    // "which phase is this step in?".
    eventBus.on("selection.changed", (event: ISelectionChangedEvent): void => {
      const selection: IShapeLike[] = event.newSelection ?? [];
      let owner: IShapeLike | undefined;
      if (selection.length === 1 && STEP_TYPES.has(selection[0].type)) {
        const centerX: number = selection[0].x + selection[0].width / 2;
        owner = this.phaseGroups().find((g: IShapeLike): boolean => centerX >= g.x && centerX < g.x + g.width);
      }
      if (this.ownerHighlight?.id === owner?.id) return;
      try {
        if (this.ownerHighlight) this.canvas.removeMarker(this.ownerHighlight, "kuika-phase-owner");
        if (owner) this.canvas.addMarker(owner, "kuika-phase-owner");
      } catch {
        // highlight is cosmetic.
      }
      this.ownerHighlight = owner;
    });

    // Groups are frame elements → diagram-js gives them a border-only ('stroke') hit, which makes
    // them hard to grab. Mirror bpmn-js's own pool-header pattern (BpmnInteractionEvents): keep the
    // border hit AND add an 'all' hit over the HEADER BAND drawn above the column, so select /
    // move / reorder all work from the header. Returning true suppresses the default hit handler.
    eventBus.on(
      ["interactionEvents.createHit", "interactionEvents.updateHit"],
      (context: IHitContext): boolean | undefined => {
        const element: IShapeLike | undefined = context.element;
        const gfx: SVGElement | undefined = context.gfx;
        if (!element || !gfx || !isPhaseGroup(element)) return undefined;
        interactionEvents.removeHits(gfx);
        interactionEvents.createDefaultHit(element, gfx);
        interactionEvents.createBoxHit(gfx, "all", {
          x: 0,
          y: -PHASE_HEADER_HEIGHT,
          width: element.width,
          height: PHASE_HEADER_HEIGHT
        });
        return true;
      }
    );

    this.postExecuted(
      ["shape.create"],
      500,
      (context: ICommandContext): void => {
        const shape: IShapeLike | undefined = context.shape;
        if (!isPhaseGroup(shape)) return;
        this.ensurePhaseIdentity(shape!);
        this.markPhaseGroup(shape!);
        // MATERIALIZATION IS NOT A DROP. `applyCreatePlacement` is the interactive rule — a
        // column dropped by hand splits whatever column its centre lands in, and the split's
        // side depends on which half it fell in. Run over a materialization it re-decides a
        // geometry that was already computed from the DECLARED order, one create at a time,
        // and the surviving left-to-right order is that algorithm's, not the author's. The
        // model then takes it back from the canvas (`syncModelFromGroups` orders by X), so the
        // declaration is overwritten in the file and a reload cannot heal it — which is exactly
        // how "Yönetici İncelemesi" ended up inside the "Muhasebe İncelemesinde" column with
        // `phaseOrder` reversed. The materializer places every column itself and needs neither
        // hook (nor the auto-name: it names at factory time).
        if (this.materializing) return;
        this.applyCreatePlacement(shape!);
        // A freshly created phase must never be nameless (palette drops arrive with no label,
        // and bpmn's create.end auto-editing skips groups). Guarded on empty name so redo /
        // materialized legacy phases (named at factory time) are not renamed.
        if (!(shape!.businessObject?.name || "").trim()) {
          this.modeling.updateLabel(shape!, nextPhaseName(this.takenPhaseNames()));
        }
        this.syncModelFromGroups();
      },
      true
    );

    // bpmn's own create.end handler auto-opens label editing only for tasks/annotations/pools —
    // groups are excluded, which left palette-dropped phases nameless and hard to rename. Open
    // direct editing ourselves. Priority 250 (below bpmn's 500) is CRITICAL: the create module
    // auto-selects the new shape from a higher-priority create.end listener, and LabelEditing
    // completes any active editing on `selection.changed` — activating too early means the editor
    // opens and is instantly closed by that selection event.
    eventBus.on("create.end", 250, (event: ICreateEndEvent): void => {
      const shape: IShapeLike | undefined = event.context?.shape;
      if (!isPhaseGroup(shape)) return;
      try {
        this.directEditing.activate(shape!);
      } catch {
        // naming convenience only.
      }
    });

    this.postExecuted(
      ["shape.delete"],
      500,
      (context: ICommandContext): void => {
        const shape: IShapeLike | undefined = context.shape;
        if (!isPhaseGroup(shape)) return;
        const phaseId: string | undefined = shape!.businessObject?.phaseId;
        const phaseName: string = phaseGroupLabel(shape!).trim() || "Phase";
        const orphanedSteps: number = this.countStepsInPhase(phaseId);
        this.removePhaseEntry(phaseId);
        this.absorbDeletedSpace(shape!); // the adjacent column takes the freed range — no other boundary moves
        this.retilePhases();
        this.syncModelFromGroups();
        // Deleting a column silently unphases its steps — say so, and point at undo. A blocking
        // confirm inside a command phase risks command-stack integrity (KOA-17362 precedent), so
        // this is a notification, not a gate.
        if (orphanedSteps > 0) {
          KMainFunctions.displayWarningNotification(
            `Phase "${phaseName}" deleted — ${orphanedSteps} step(s) are now unphased. Undo with Ctrl+Z.`
          );
        }
      },
      true
    );

    // UNDO of a phase creation reverts shape.create WITHOUT a shape.delete command — without this
    // hook the model entry would survive as an orphan and re-materialize as a column on next load.
    this.reverted(
      ["shape.create"],
      500,
      (context: ICommandContext): void => {
        const shape: IShapeLike | undefined = context.shape;
        if (!isPhaseGroup(shape)) return;
        this.removePhaseEntry(shape!.businessObject?.phaseId);
        this.absorbDeletedSpace(shape!);
        this.retilePhases();
        this.syncModelFromGroups();
      },
      true
    );

    // Safety net for every path postExecuted cannot see — most importantly UNDO/REDO (they fire no
    // post hooks, so Ctrl+Z of a step move would otherwise leave the metadata on the NEW phase
    // while the shape is back on the old one). syncModelFromGroups only writes the JSON model
    // (never executes diagram commands) and saves only when something actually changed, so this
    // listener cannot loop and is cheap when nothing moved.
    eventBus.on("commandStack.changed", (): void => {
      if (this.pinning) return;
      this.syncModelFromGroups();
    });

    this.postExecuted(
      ["element.updateLabel"],
      500,
      (context: ICommandContext): void => {
        const element: IShapeLike | undefined = context.element;
        if (!isPhaseGroup(element)) return;
        // The label element is CREATED by the first updateLabel (a fresh group has none), and a
        // rename changes its size — (re-)centre it in the header band in both cases. Without this
        // a palette-dropped phase's label sat at bpmn's default position until the column moved.
        this.positionLabelInHeader(element!);
        this.syncModelFromGroups();
      },
      true
    );

    // Resizing a phase moves the SHARED boundary: the neighbour on the resized edge gives or
    // takes the difference, so the split always tiles the pool (no gaps, no overlap).
    this.postExecuted(
      ["shape.resize"],
      500,
      (context: ICommandContext): void => {
        if (this.pinning || context.hints?.kuikaPhasePin) return;
        const shape: IShapeLike | undefined = context.shape;
        const resizedPhase: boolean = isPhaseGroup(shape);
        const poolChanged: boolean = shape?.type === "bpmn:Participant" || shape?.type === "bpmn:Lane";
        if (!resizedPhase && !poolChanged) return;
        if (resizedPhase && context.oldBounds) {
          this.retilePhases({ shape: shape!, oldBounds: context.oldBounds });
        } else {
          this.retilePhases();
        }
        this.syncModelFromGroups();
      },
      true
    );

    // Moving a phase column = REORDERING it (strict tiling leaves no free space to move into):
    // the retile sorts columns by centre and snaps them back into the contiguous split.
    this.postExecuted(
      ["shape.move", "elements.move"],
      500,
      (context: ICommandContext): void => {
        if (this.pinning || context.hints?.kuikaPhasePin) return;
        const moved: IShapeLike[] = context.shapes ?? (context.shape ? [context.shape] : []);
        const poolChanged: boolean = moved.some(
          (s: IShapeLike): boolean => s.type === "bpmn:Participant" || s.type === "bpmn:Lane"
        );
        const phaseMoved: boolean = moved.some((s: IShapeLike): boolean => isPhaseGroup(s));
        const movedSteps: IShapeLike[] = moved.filter((s: IShapeLike): boolean => STEP_TYPES.has(s.type));
        if (!phaseMoved && !poolChanged && movedSteps.length === 0) return;
        if (phaseMoved || poolChanged) this.retilePhases();
        for (const step of movedSteps) this.snapStepClearOfEdges(step);
        this.syncModelFromGroups();
      },
      true
    );

    // Live drop-target preview: while a step is dragged, highlight the phase column that would
    // own it (by centre-X) so the user sees the assignment before releasing.
    eventBus.on("shape.move.move", (event: IMoveEvent): void => {
      const shape: IShapeLike | undefined = event.shape;
      if (!shape || !STEP_TYPES.has(shape.type) || typeof event.x !== "number") return;
      const centerX: number = event.x + shape.width / 2;
      const target: IShapeLike | undefined = this.phaseGroups().find(
        (g: IShapeLike): boolean => centerX >= g.x && centerX < g.x + g.width
      );
      this.setDropTarget(target);
    });
    eventBus.on(["shape.move.end", "shape.move.cleanup"], (): void => this.setDropTarget(undefined));

    // Reconcile both directions once the diagram is loaded (legacy phases → groups, groups → entries).
    eventBus.on("import.done", (): void => {
      try {
        this.materializeMissingGroups();
        for (const group of this.phaseGroups()) this.markPhaseGroup(group);
        this.retilePhases(); // stored XML may pre-date strict tiling — snap the split to the pool
        this.syncModelFromGroups();
      } catch {
        // reconcile is best-effort; a failure must never block diagram load.
      }
    });
  }

  /**
   * Programmatic one-click phase creation (public API — e.g. for MCP/DSL parity; the palette's
   * Happy Path category is the interactive path). STRICT TILING semantics: the first phase covers
   * the whole pool; afterwards adding a phase SPLITS the last column in half
   * (`applyCreatePlacement` positions by the created shape's centre = the new boundary).
   */
  addPhaseAtEnd(): void {
    const pool: IBounds | undefined = this.poolBounds();
    if (!pool) return;
    const groups: IShapeLike[] = this.phaseGroups();
    const last: IShapeLike | undefined = groups[groups.length - 1];
    if (last && last.width < MIN_PHASE_WIDTH * 2) return; // no room to split further

    // Centre of the created shape = the split boundary: pool centre for the first phase (covers
    // everything anyway), the last column's midpoint otherwise (halves it).
    const boundaryX: number = last ? last.x + last.width / 2 : pool.x + pool.width / 2;
    const width: number = last ? last.width / 2 : pool.width;
    const shape: IShapeLike = this.newPhaseShape(pool, width);
    this.modeling.createShape(shape, { x: boundaryX, y: pool.y + pool.height / 2 }, this.canvas.getRootElement());
    // Default name is applied by the shape.create hook; open direct editing with it selected so
    // typing replaces it and Enter/Escape keeps it.
    try {
      this.directEditing.activate(shape);
    } catch {
      // naming convenience only — never let it break creation.
    }
  }

  /** Toggle the drop-target highlight marker (at most one column highlighted at a time). */
  private setDropTarget(target: IShapeLike | undefined): void {
    if (this.dropTarget?.id === target?.id) return;
    try {
      if (this.dropTarget) this.canvas.removeMarker(this.dropTarget, "kuika-phase-drop-target");
      if (target) this.canvas.addMarker(target, "kuika-phase-drop-target");
    } catch {
      // highlight is cosmetic — never let it break a drag.
    }
    this.dropTarget = target;
  }

  /**
   * STRICT TILING placement for a newly created phase: the first phase covers the whole pool;
   * any further phase SPLITS the column under the created shape's centre — the centre IS the new
   * boundary (clamped so both parts keep the minimum width). The new phase takes the side of the
   * boundary its centre leans toward relative to the host's midpoint, so a palette drop near a
   * column's left edge creates the new phase on the left. A retile follows for exactness.
   */
  private applyCreatePlacement(shape: IShapeLike): void {
    const pool: IBounds | undefined = this.poolBounds();
    if (!pool) return;
    const others: IShapeLike[] = this.phaseGroups().filter((g: IShapeLike): boolean => g.id !== shape.id);

    this.pinning = true;
    try {
      if (others.length === 0) {
        // First phase: cover the whole pool ("directly cover the whole lane").
        this.modeling.resizeShape(shape, { x: pool.x, y: pool.y, width: pool.width, height: pool.height }, null, {
          kuikaPhasePin: true
        });
        return;
      }

      const dropX: number = Math.max(
        pool.x + MIN_PHASE_WIDTH,
        Math.min(shape.x + shape.width / 2, pool.x + pool.width - MIN_PHASE_WIDTH)
      );
      const host: IShapeLike =
        others.find((g: IShapeLike): boolean => dropX >= g.x && dropX < g.x + g.width) ??
        (dropX < others[0].x ? others[0] : others[others.length - 1]);
      const hostRight: number = host.x + host.width;
      const boundary: number = Math.max(host.x + MIN_PHASE_WIDTH, Math.min(dropX, hostRight - MIN_PHASE_WIDTH));
      const newTakesLeft: boolean = dropX < host.x + host.width / 2;

      const hostBounds: IBounds = newTakesLeft
        ? { x: boundary, y: pool.y, width: hostRight - boundary, height: pool.height }
        : { x: host.x, y: pool.y, width: boundary - host.x, height: pool.height };
      const newBounds: IBounds = newTakesLeft
        ? { x: host.x, y: pool.y, width: boundary - host.x, height: pool.height }
        : { x: boundary, y: pool.y, width: hostRight - boundary, height: pool.height };

      this.modeling.resizeShape(host, hostBounds, null, { kuikaPhasePin: true });
      this.modeling.resizeShape(shape, newBounds, null, { kuikaPhasePin: true });
    } finally {
      this.pinning = false;
    }
    this.retilePhases();
  }

  /**
   * When a phase is deleted (or its creation undone), hand its range to the ADJACENT column —
   * the nearest left neighbour, or the first right neighbour when the leftmost phase was deleted.
   * This keeps every other boundary where it was, so no step changes phase as a side effect
   * (the following retile then only normalizes rounding). `deleted` is the removed shape from the
   * command context — it still carries its last bounds even though it left the registry.
   */
  private absorbDeletedSpace(deleted: IShapeLike): void {
    const pool: IBounds | undefined = this.poolBounds();
    if (!pool) return;
    const groups: IShapeLike[] = this.phaseGroups();
    if (groups.length === 0) return;

    const leftNeighbours: IShapeLike[] = groups.filter((g: IShapeLike): boolean => g.x + g.width <= deleted.x + 1);
    const left: IShapeLike | undefined = leftNeighbours[leftNeighbours.length - 1];
    const right: IShapeLike | undefined = groups.find((g: IShapeLike): boolean => g.x >= deleted.x - 1);

    this.pinning = true;
    try {
      if (left) {
        this.modeling.resizeShape(
          left,
          { x: left.x, y: pool.y, width: left.width + deleted.width, height: pool.height },
          null,
          { kuikaPhasePin: true }
        );
      } else if (right) {
        this.modeling.resizeShape(
          right,
          { x: deleted.x, y: pool.y, width: right.width + deleted.width, height: pool.height },
          null,
          { kuikaPhasePin: true }
        );
      }
    } finally {
      this.pinning = false;
    }
  }

  /**
   * STRICT TILING: re-lay every phase column so the split covers the pool exactly — ordered by
   * centre (dragging a column past another reorders it), contiguous from the pool's left edge to
   * its right, full pool height. Widths are preserved proportionally; when a `resized` context is
   * given, the resized edge first shifts the SHARED boundary (the neighbour gives/takes the
   * difference) so resizing feels like dragging the divider between two phases.
   */
  private retilePhases(resized?: { shape: IShapeLike; oldBounds: IBounds }): void {
    const pool: IBounds | undefined = this.poolBounds();
    if (!pool || pool.width <= 0) return;
    const ordered: IShapeLike[] = this.phaseGroups().sort(
      (a: IShapeLike, b: IShapeLike): number => a.x + a.width / 2 - (b.x + b.width / 2)
    );
    if (ordered.length === 0) return;

    const widths: number[] = ordered.map((g: IShapeLike): number => Math.max(1, g.width));

    // Boundary semantics for a resize: the moved edge takes space from / gives space to the
    // neighbour on that side, keeping the pair's total constant.
    if (resized) {
      const index: number = ordered.findIndex((g: IShapeLike): boolean => g.id === resized.shape.id);
      if (index >= 0) {
        const dLeft: number = resized.shape.x - resized.oldBounds.x;
        const dRight: number = resized.shape.x + resized.shape.width - (resized.oldBounds.x + resized.oldBounds.width);
        if (Math.abs(dRight) > 0.5 && index < ordered.length - 1) {
          widths[index + 1] = Math.max(MIN_PHASE_WIDTH, widths[index + 1] - dRight);
        }
        if (Math.abs(dLeft) > 0.5 && index > 0) {
          widths[index - 1] = Math.max(MIN_PHASE_WIDTH, widths[index - 1] + dLeft);
        }
      }
    }

    // Fit to the pool WITHOUT rescaling everyone: earlier columns keep their width, the LAST
    // column absorbs the difference (a longer lane only grows the last phase). When the pool
    // shrinks below what the fixed widths need, the deficit is borrowed right-to-left down to the
    // minimum width — the leftmost phases are disturbed last.
    const scaled: number[] = widths.slice();
    const sumOthers: number = scaled.slice(0, -1).reduce((sum: number, w: number): number => sum + w, 0);
    scaled[scaled.length - 1] = pool.width - sumOthers;
    if (scaled.length > 1 && scaled[scaled.length - 1] < MIN_PHASE_WIDTH) {
      let deficit: number = MIN_PHASE_WIDTH - scaled[scaled.length - 1];
      scaled[scaled.length - 1] = MIN_PHASE_WIDTH;
      for (let i = scaled.length - 2; i >= 0 && deficit > 0; i--) {
        const give: number = Math.min(deficit, Math.max(0, scaled[i] - MIN_PHASE_WIDTH));
        scaled[i] -= give;
        deficit -= give;
      }
      // Pool genuinely too small for n minimum-width columns: fall back to an equal split.
      if (deficit > 0) {
        for (let i = 0; i < scaled.length; i++) scaled[i] = pool.width / scaled.length;
      }
    }

    // Place contiguously; the last column absorbs rounding so the split always ends at the pool edge.
    this.pinning = true;
    try {
      let x: number = pool.x;
      ordered.forEach((group: IShapeLike, i: number): void => {
        const isLast: boolean = i === ordered.length - 1;
        const width: number = isLast ? pool.x + pool.width - x : scaled[i];
        const changed: boolean =
          Math.abs(group.x - x) > 0.5 ||
          Math.abs(group.width - width) > 0.5 ||
          Math.abs(group.y - pool.y) > 0.5 ||
          Math.abs(group.height - pool.height) > 0.5;
        if (changed) {
          this.modeling.resizeShape(group, { x, y: pool.y, width, height: pool.height }, null, {
            kuikaPhasePin: true
          });
        }
        x += width;
      });
    } finally {
      this.pinning = false;
    }
    for (const group of ordered) this.positionLabelInHeader(group);
  }

  private newPhaseShape(pool: IBounds, width: number = DEFAULT_PHASE_WIDTH): IShapeLike {
    const bo: IBusinessObject = this.bpmnFactory.create("bpmn:Group");
    bo.isHappyPathPhase = true;
    bo.phaseId = Guid.create().toString();
    return this.elementFactory.createShape({
      type: "bpmn:Group",
      businessObject: bo,
      width,
      height: pool.height
    });
  }

  private ensurePhaseIdentity(shape: IShapeLike): void {
    const bo: IBusinessObject | undefined = shape.businessObject;
    if (bo && !bo.phaseId) bo.phaseId = Guid.create().toString();
  }

  /**
   * Names already owned by a phase — the groups on canvas AND the model entries (a group-less
   * legacy entry from the panel still owns its name), so a new default never collides with either.
   */
  private takenPhaseNames(): string[] {
    const taken: string[] = this.phaseGroups().map((g: IShapeLike): string => phaseGroupLabel(g));
    const wf: IWorkFlow | undefined = this.workflow();
    if (Array.isArray(wf?.happyPathDefinition)) {
      for (const entry of wf!.happyPathDefinition) taken.push(entry.happyPathPhase || "");
    }
    return taken;
  }

  /** Number of steps currently assigned to the given phase (recursive, matches panel semantics). */
  private countStepsInPhase(phaseId: string | undefined): number {
    const wf: IWorkFlow | undefined = this.workflow();
    if (!wf || !phaseId || !wf.nodes?.steps) return 0;
    let count: number = 0;
    this.providerInstance.workflowProvider.iterateAllStepsRecursively(wf.nodes.steps, (step): void => {
      if (getDictionaryValueByKey(step.extraParameters, PHASE_KEY) === phaseId) count++;
    });
    return count;
  }

  /** CSS marker so the theme can style phase groups (e.g. stay transparent when selected). */
  private markPhaseGroup(shape: IShapeLike): void {
    try {
      this.canvas.addMarker(shape, "kuika-phase-group");
    } catch {
      // marker is purely cosmetic — never let it break a command.
    }
  }

  private poolBounds(): IBounds | undefined {
    const pool: IShapeLike | undefined = this.elementRegistry.filter(
      (e: IShapeLike): boolean => e.type === "bpmn:Participant"
    )[0];
    if (!pool || pool.height < MIN_POOL_HEIGHT) return undefined;
    return { x: pool.x, y: pool.y, width: pool.width, height: pool.height };
  }

  private phaseGroups(): IShapeLike[] {
    return this.elementRegistry
      .filter((e: IShapeLike): boolean => isPhaseGroup(e))
      .sort((a: IShapeLike, b: IShapeLike): number => a.x - b.x);
  }

  /**
   * Keep the group's external label centred in the header band above the column — the label is the
   * header's title (single source of truth; double-click rename opens right there). Runs after
   * every normalize, so moves/resizes re-centre it.
   */
  private positionLabelInHeader(group: IShapeLike): void {
    const label: IShapeLike | undefined = group.label;
    if (!label) return;
    const targetX: number = group.x + group.width / 2 - label.width / 2;
    const targetY: number = group.y - PHASE_HEADER_HEIGHT / 2 - label.height / 2;
    const dx: number = targetX - label.x;
    const dy: number = targetY - label.y;
    if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;
    this.pinning = true;
    try {
      this.modeling.moveShape(label, { x: dx, y: dy }, null, { kuikaPhasePin: true });
    } finally {
      this.pinning = false;
    }
  }

  /**
   * A step dropped straddling a phase-column edge is ambiguous — resolve it by nudging the step
   * minimally: fully INTO the column that owns its centre, or fully OUT of a column it merely
   * overlaps while its centre lies outside (defensive: strict tiling normally leaves no gaps).
   */
  private snapStepClearOfEdges(step: IShapeLike): void {
    const groups: IShapeLike[] = this.phaseGroups();
    if (groups.length === 0) return;
    const centerX: number = step.x + step.width / 2;
    const owner: IShapeLike | undefined = groups.find(
      (g: IShapeLike): boolean => centerX >= g.x && centerX < g.x + g.width
    );

    let newX: number = step.x;
    if (owner) {
      const min: number = owner.x + STEP_SNAP_PAD;
      const max: number = owner.x + owner.width - step.width - STEP_SNAP_PAD;
      if (max < min) {
        newX = owner.x + (owner.width - step.width) / 2; // column narrower than the step — centre it
      } else {
        newX = Math.max(min, Math.min(step.x, max));
      }
    } else {
      const overlapped: IShapeLike | undefined = groups.find(
        (g: IShapeLike): boolean => step.x < g.x + g.width && step.x + step.width > g.x
      );
      if (overlapped) {
        newX =
          centerX < overlapped.x
            ? overlapped.x - step.width - STEP_SNAP_PAD
            : overlapped.x + overlapped.width + STEP_SNAP_PAD;
      }
    }

    if (Math.abs(newX - step.x) < 1) return;
    this.pinning = true;
    try {
      this.modeling.moveShape(step, { x: newX - step.x, y: 0 }, null, { kuikaPhasePin: true });
    } finally {
      this.pinning = false;
    }
  }

  // ---- model sync (same write path as the Happy Path panel) -----------------------------------

  private workflow(): IWorkFlow | undefined {
    return this.providerInstance.workflowProvider.state.selectedWf;
  }

  private removePhaseEntry(phaseId: string | undefined): void {
    const wf: IWorkFlow | undefined = this.workflow();
    if (!wf || !phaseId || !Array.isArray(wf.happyPathDefinition)) return;
    const index: number = wf.happyPathDefinition.findIndex((p: IHappyPathPhaseModel): boolean => p.id === phaseId);
    if (index < 0) return;
    wf.happyPathDefinition.splice(index, 1);
    if (wf.nodes?.steps) {
      this.providerInstance.workflowProvider.iterateAllStepsRecursively(wf.nodes.steps, (step): void => {
        if (getDictionaryValueByKey(step.extraParameters, PHASE_KEY) === phaseId) {
          step.extraParameters = addToDictionary(step.extraParameters, PHASE_KEY, "");
        }
      });
    }
  }

  /**
   * The groups on canvas are the source of truth: upsert an entry per phase group (name from its
   * label, order by X), drop entries whose group is gone, and re-assign every step by centre-X.
   * Saves once, and only when something actually changed.
   */
  private syncModelFromGroups(): void {
    const wf: IWorkFlow | undefined = this.workflow();
    if (!wf) return;
    const groups: IShapeLike[] = this.phaseGroups();
    if (!Array.isArray(wf.happyPathDefinition)) wf.happyPathDefinition = [];
    const definition: IHappyPathPhaseModel[] = wf.happyPathDefinition;
    let dirty: boolean = false;

    // Upsert one entry per group, ordered left-to-right.
    groups.forEach((group: IShapeLike, i: number): void => {
      this.ensurePhaseIdentity(group);
      const phaseId: string = group.businessObject!.phaseId!;
      const name: string = phaseGroupLabel(group).trim() || `Phase ${i + 1}`;
      const order: number = i + 1;
      const entry: IHappyPathPhaseModel | undefined = definition.find(
        (p: IHappyPathPhaseModel): boolean => p.id === phaseId
      );
      if (!entry) {
        definition.push({ id: phaseId, happyPathPhase: name, phaseOrder: order });
        dirty = true;
        return;
      }
      if (entry.happyPathPhase !== name) {
        entry.happyPathPhase = name;
        dirty = true;
      }
      if (entry.phaseOrder !== order) {
        entry.phaseOrder = order;
        dirty = true;
      }
    });

    // NOTE: entries with no matching group are intentionally KEPT — an entry is removed only by
    // the explicit shape.delete handler (removePhaseEntry) or by the Happy Path panel. Dropping
    // group-less entries here would wipe legitimate legacy phases whenever materialization was
    // skipped (e.g. a pool-less diagram) and the user then touched any phase group.

    // NO COLUMNS IS NOT AN ANSWER — it is the absence of one.
    //
    // The assignment below reads a step's phase off the column containing its centre, so with an
    // EMPTY column set every step resolves to "" and every assignment is wiped. That is correct
    // when the model declares no phases (the user just deleted the last column — `removePhaseEntry`
    // has already emptied the definition, and the delete handler tells them their steps are now
    // unphased). It is catastrophic in the other case: a SCRIPT declares the phases and assigns
    // steps in the MODEL, and the columns do not exist until the diagram next loads. This sync runs
    // on `commandStack.changed`, i.e. on the very next node the script creates — so `PHASE_ASSIGN`
    // was erased seconds after it was written, and the assignments the process shipped with were
    // whatever geometry later invented.
    //
    // So: when the model declares phases the canvas has not materialized yet, the canvas cannot
    // answer the question and must not be read as answering "none". Same rule this codebase already
    // learned about an unreadable canvas (KOA-18136 §37) — one empty collection cannot mean both
    // "I cannot see" and "there is nothing".
    const declaresPhases: boolean = Array.isArray(wf.happyPathDefinition) && wf.happyPathDefinition.length > 0;
    if (groups.length === 0 && declaresPhases) {
      if (dirty) {
        this.providerInstance.workflowProvider.saveWorkFlow(wf, ESdChangeReason.WFStepMappingChange);
      }
      return;
    }

    // Assign steps by centre-X (strict tiling covers the pool, so an in-pool step always has a
    // phase; only a step outside the pool — or with no phases defined — is unphased ""). Recurse into
    // subprocess steps too — same coverage the retired panel had via iterateAllStepsRecursively.
    const counts: Map<string, number> = new Map<string, number>();
    if (wf.nodes?.steps) {
      this.providerInstance.workflowProvider.iterateAllStepsRecursively(wf.nodes.steps, (step): void => {
        const shape: IShapeLike | undefined = this.elementRegistry.get(step.id);
        if (!shape) return;
        const centerX: number = shape.x + shape.width / 2;
        const owner: IShapeLike | undefined = groups.find(
          (g: IShapeLike): boolean => centerX >= g.x && centerX < g.x + g.width
        );
        const target: string = owner ? (owner.businessObject?.phaseId ?? "") : "";
        if (target) counts.set(target, (counts.get(target) ?? 0) + 1);
        const current: string = getDictionaryValueByKey(step.extraParameters, PHASE_KEY) || "";
        if (current !== target) {
          step.extraParameters = addToDictionary(step.extraParameters, PHASE_KEY, target);
          dirty = true;
        }
      });
    }
    this.updateCountBadges(groups, counts);
    this.updateBoundaryGrips(groups);

    if (dirty) {
      this.providerInstance.workflowProvider.saveWorkFlow(wf, ESdChangeReason.WFStepMappingChange);
    }
  }

  /**
   * A dedicated drag GRIP on every internal boundary, rendered in the header row (an overlay
   * anchored to the left column's right edge, so it tracks resizes for free). Dragging it moves
   * the shared boundary between the two adjacent phases — this is THE resize interaction for
   * phases; the native bpmn resize handles are disabled for phase groups (CustomBpmnRules), which
   * were fiddly (select first, grab a tiny corner) and could not express boundary semantics.
   */
  private updateBoundaryGrips(groups: IShapeLike[]): void {
    const key: string = groups.map((g: IShapeLike): string => g.id).join("|");
    if (key === this.boundaryHandlesKey) return;
    this.boundaryHandlesKey = key;
    try {
      this.overlays.remove({ type: "kuika-hpp-boundary" });
      for (let i = 0; i < groups.length - 1; i++) {
        const left: IShapeLike = groups[i];
        const grip: HTMLElement = document.createElement("div");
        grip.className = "kuika-hpp-boundary-grip";
        grip.title = "Drag to resize phases";
        grip.addEventListener("pointerdown", (e: PointerEvent): void => this.onGripDown(e, left.id, grip));
        this.overlays.add(left, "kuika-hpp-boundary", {
          position: { top: -PHASE_HEADER_HEIGHT, right: 5 },
          html: grip,
          scale: true
        });
      }
    } catch {
      // grips are an affordance — never let them break a sync.
    }
  }

  private onGripDown(e: PointerEvent, leftId: string, handleEl: HTMLElement): void {
    e.preventDefault();
    e.stopPropagation();
    const groups: IShapeLike[] = this.phaseGroups();
    const index: number = groups.findIndex((g: IShapeLike): boolean => g.id === leftId);
    const left: IShapeLike | undefined = groups[index];
    const right: IShapeLike | undefined = groups[index + 1];
    const pool: IBounds | undefined = this.poolBounds();
    if (!left || !right || !pool) return;
    this.boundaryDrag = {
      leftId,
      startClientX: e.clientX,
      leftStartX: left.x,
      leftStartWidth: left.width,
      pairWidth: left.width + right.width,
      scale: this.canvas.viewbox().scale || 1,
      handleEl,
      poolTop: pool.y - PHASE_HEADER_HEIGHT,
      poolBottom: pool.y + pool.height
    };
    window.addEventListener("pointermove", this.onGripMove);
    window.addEventListener("pointerup", this.onGripUp);
  }

  /** Live boundary preview: a guide line in a canvas layer (native zoom/pan alignment). */
  private updatePreviewLine(boundaryX: number, drag: IBoundaryDrag): void {
    try {
      const layer: SVGElement = this.canvas.getLayer("kuika-hpp-preview", 2);
      if (!this.previewLine) {
        this.previewLine = document.createElementNS("http://www.w3.org/2000/svg", "line");
        this.previewLine.setAttribute("class", "kuika-hpp-boundary-preview");
        layer.appendChild(this.previewLine);
      }
      this.previewLine.setAttribute("x1", String(boundaryX));
      this.previewLine.setAttribute("x2", String(boundaryX));
      this.previewLine.setAttribute("y1", String(drag.poolTop));
      this.previewLine.setAttribute("y2", String(drag.poolBottom));
    } catch {
      // preview is cosmetic.
    }
  }

  private clearPreviewLine(): void {
    try {
      this.previewLine?.remove();
    } catch {
      // preview is cosmetic.
    }
    this.previewLine = null;
  }

  /** Clamp the boundary shift so both columns keep the minimum width. */
  private gripDelta(e: PointerEvent, drag: IBoundaryDrag): number {
    const raw: number = (e.clientX - drag.startClientX) / drag.scale;
    return Math.max(
      MIN_PHASE_WIDTH - drag.leftStartWidth,
      Math.min(raw, drag.pairWidth - MIN_PHASE_WIDTH - drag.leftStartWidth)
    );
  }

  private onGripMove = (e: PointerEvent): void => {
    const drag: IBoundaryDrag | null = this.boundaryDrag;
    if (!drag) return;
    const delta: number = this.gripDelta(e, drag);
    // The overlay is scale:true (its container is zoom-scaled), so local px = diagram units.
    drag.handleEl.style.transform = `translateX(${delta}px)`;
    // …and a full-height guide line shows where the boundary will land on release.
    this.updatePreviewLine(drag.leftStartX + drag.leftStartWidth + delta, drag);
  };

  private onGripUp = (e: PointerEvent): void => {
    const drag: IBoundaryDrag | null = this.boundaryDrag;
    this.boundaryDrag = null;
    window.removeEventListener("pointermove", this.onGripMove);
    window.removeEventListener("pointerup", this.onGripUp);
    this.clearPreviewLine();
    if (!drag) return;
    drag.handleEl.style.transform = "";
    const delta: number = this.gripDelta(e, drag);
    if (Math.abs(delta) < 1) return;
    const pool: IBounds | undefined = this.poolBounds();
    const left: IShapeLike | undefined = this.elementRegistry.get(drag.leftId);
    if (!pool || !left) return;
    // Commit as a plain (un-pinned) resize of the left column — the shape.resize hook then shifts
    // the shared boundary by compensating the right neighbour and retiling, same as any resize.
    this.modeling.resizeShape(left, {
      x: left.x,
      y: pool.y,
      width: drag.leftStartWidth + delta,
      height: pool.height
    });
  };

  /** Step-count badge in each column's header — refreshed only when the counts actually change. */
  private updateCountBadges(groups: IShapeLike[], counts: Map<string, number>): void {
    const key: string = groups
      .map((g: IShapeLike): string => `${g.id}:${counts.get(g.businessObject?.phaseId ?? "") ?? 0}`)
      .join("|");
    if (key === this.lastCountsKey) return;
    this.lastCountsKey = key;
    try {
      this.overlays.remove({ type: "kuika-hpp-count" });
      for (const group of groups) {
        const count: number = counts.get(group.businessObject?.phaseId ?? "") ?? 0;
        // No badge for an empty phase — a row of zeros next to the titles reads like an order /
        // index indicator and confused QA into reporting them as broken order numbers (KOA-18280).
        if (count === 0) continue;
        this.overlays.add(group, "kuika-hpp-count", {
          position: { top: -PHASE_HEADER_HEIGHT + 5, right: 24 },
          html: `<div class="kuika-hpp-count" title="${count} step(s) in this phase">${count}</div>`,
          scale: true
        });
      }
    } catch {
      // badges are cosmetic — never let them break a sync.
    }
  }

  /** Legacy workflows: phases defined via the panel but with no groups yet → tile groups over the pool. */
  private materializeMissingGroups(): void {
    const wf: IWorkFlow | undefined = this.workflow();
    const pool: IBounds | undefined = this.poolBounds();
    if (!wf || !pool || !Array.isArray(wf.happyPathDefinition) || wf.happyPathDefinition.length === 0) return;

    const existing: IShapeLike[] = this.phaseGroups();
    const existingIds: Set<string> = new Set<string>(
      existing.map((g: IShapeLike): string => g.businessObject?.phaseId ?? "")
    );
    const ordered: IHappyPathPhaseModel[] = wf.happyPathDefinition
      .slice()
      .sort((a: IHappyPathPhaseModel, b: IHappyPathPhaseModel): number => a.phaseOrder - b.phaseOrder);
    const missing: IHappyPathPhaseModel[] = ordered.filter(
      (p: IHappyPathPhaseModel): boolean => !existingIds.has(p.id)
    );
    if (missing.length === 0) return;

    this.materializing = true;
    try {
      // THE WHOLE SET IS BEING BORN — place it from the DECLARATION, not from an even split.
      //
      // A model-declared process (a script authored the phases and assigned steps to them) has
      // no columns until this moment, so nothing on canvas has an opinion yet. Deriving the
      // boundaries from where the DECLARED steps actually sit means every such step lands inside
      // its own column, and the geometric re-derivation that follows AGREES with the declaration
      // instead of overwriting it. An even split cannot promise that: it puts the boundaries
      // wherever thirds happen to fall, and the first step to sit on the wrong side of one takes
      // its whole phase with it.
      //
      // Steps are never moved here. The columns follow the diagram; the diagram is not bent to
      // fit the columns.
      if (existing.length === 0) {
        const planned: IPhaseLayoutPlan = planPhaseColumns(
          ordered.map((p: IHappyPathPhaseModel): IPhaseDeclaration => ({
            id: p.id,
            name: p.happyPathPhase,
            order: p.phaseOrder
          })),
          this.declaredStepGeometry(wf),
          { x: pool.x, width: pool.width }
        );
        if (planned.columns.length === ordered.length) {
          for (const column of planned.columns) {
            const phase: IHappyPathPhaseModel | undefined = ordered.find(
              (p: IHappyPathPhaseModel): boolean => p.id === column.phaseId
            );
            if (!phase) continue;
            this.createPhaseGroup(phase, { x: column.x, y: pool.y, width: column.width, height: pool.height });
          }
          this.syncModelFromGroups();
          return;
        }
      }

      // Fallback: some columns already exist (or the pool is too narrow to plan), so only the
      // missing ones are added, evenly, left to right in declared order.
      const width: number = Math.max(MIN_PHASE_WIDTH, pool.width / wf.happyPathDefinition.length);
      let x: number = pool.x;
      for (const phase of missing) {
        this.createPhaseGroup(phase, { x, y: pool.y, width, height: pool.height });
        x += width;
      }
      this.syncModelFromGroups();
    } finally {
      this.materializing = false;
    }
  }

  /**
   * Every step that DECLARES a phase, with the geometry it currently has on canvas. The input the
   * column planner needs: it answers "which X range does each phase own" from where its own steps
   * already are.
   */
  private declaredStepGeometry(wf: IWorkFlow): IPhaseLayoutStep[] {
    const steps: IPhaseLayoutStep[] = [];
    const nodes = wf.nodes;
    if (!nodes || !Array.isArray(nodes.steps)) return steps;
    this.providerInstance.workflowProvider.iterateAllStepsRecursively(nodes.steps, (step): void => {
      const phaseId: string = getDictionaryValueByKey(step.extraParameters, PHASE_KEY) ?? "";
      if (phaseId.length === 0) return;
      const shape: IShapeLike | undefined = this.elementRegistry.get(String(step.id));
      if (!shape) return;
      steps.push({ id: String(step.id), phaseId, x: shape.x, width: shape.width });
    });
    return steps;
  }

  /** Create one phase column at an EXACT rectangle, named at factory time so no auto-name fires. */
  private createPhaseGroup(phase: IHappyPathPhaseModel, bounds: IBounds): void {
    const bo: IBusinessObject = this.bpmnFactory.create("bpmn:Group");
    bo.isHappyPathPhase = true;
    bo.phaseId = phase.id;
    bo.name = phase.happyPathPhase;
    const shape: IShapeLike = this.elementFactory.createShape({
      type: "bpmn:Group",
      businessObject: bo,
      width: bounds.width,
      height: bounds.height
    });
    this.modeling.createShape(
      shape,
      { x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height / 2 },
      this.canvas.getRootElement()
    );
    this.modeling.updateLabel(shape, phase.happyPathPhase);
  }
}

HappyPathPhaseInterceptor.$inject = [
  "eventBus",
  "modeling",
  "elementRegistry",
  "elementFactory",
  "bpmnFactory",
  "canvas",
  "directEditing",
  "interactionEvents",
  "overlays",
  "providerInstance"
];
